import * as React from 'react';

/**
 * This hook is responsible for initializing the legacy design system components after React rehydration has been complete.
 *
 * Why we need to wait until rehydration is complete?
 * - The legacy design system traverses the DOM in the seek of data attributes that identify the html pieces that are part
 *   of a component.
 * - When a component is found, the JS code attaches to the DOM that corresponds to the component and starts controlling
 *   it.
 * - In some cases this initialization process mutates the DOM causing a mismatch between what was generated in the
 *   server.
 * - If this happens before rehydration, react detects such mismatch and yields rehydration-related warnings.
 * - This can potentially affect the performance of the page.
 *
 * How this hook works?
 * - When `window.sunwebgroup_legacy_design_system.REACT_REHYDRATION_MODE_ENABLED` is set to true (see _document.tsx)
 *   the JS design system does not initialize, instead it exposes a function to the global window object that would
 *   trigger the initialization process.
 * - This way we can call that function from React when we know the rehydration precess has been completed (this is when
 *   React effects are executed).
 *
 */
export function useInitLegacyDesignSystemAfterRehydration() {
  const didRunRef = React.useRef(false);

  React.useEffect(() => {
    if (didRunRef.current) return;

    window?.sunwebgroup_legacy_design_system?.init_legacy_components?.();
    didRunRef.current = true;
  }, []);
}
